import { message } from 'antd';
import { produce } from 'immer';
import { compressPdf } from '../requests/proposals';
import { formatPagesPreview, mutateClosePreview } from '../utils/pagesPreview';
import { specificPdfErrorMessage } from '../utils/pdfErrorMessage';

const openPdfWindow = (pdfUrl) => {
  const newWindow = window.open(pdfUrl, '_blank', 'noopener,noreferrer');
  if (newWindow) {
    newWindow.focus();
    newWindow.opener = null;
  }
};

export const pdfActions = (set, get) => ({
  handlePdfReponse: (data) => {
    set(
      produce((draft) => {
        try {
          if (draft.isPdfCompressing) {
            // Résultat de la compression demandée, reçu via le websocket
            if (data?.pdf_url) {
              message.success({
                content:
                  data.compressed === false
                    ? "Le PDF n'a pas pu être réduit davantage."
                    : 'Le PDF a bien été compressé.',
                key: 'pdfKey',
              });
              openPdfWindow(data.pdf_url);
            } else if (data?.pdf_error) {
              message.error({
                content:
                  'La compression du PDF a échoué, le PDF original a été ouvert.',
                key: 'pdfKey',
              });
              if (draft.pdfCompression?.pdfUrl)
                openPdfWindow(draft.pdfCompression.pdfUrl);
            }
            draft.pdfCompression = null;
            draft.isPdfCompressing = false;
            return;
          }
          if (data?.pdf_url) {
            if (draft.PDFRenderState === 'archive')
              message.success({
                content: 'Le PDF a bien été enregistré.',
                key: 'pdfKey',
              });
            else
              message.success({
                content: 'Le PDF a bien été généré.',
                key: 'pdfKey',
              });
            if (data.needs_compression) {
              draft.pdfCompression = {
                pdfUrl: data.pdf_url,
                sizeBytes: data.size_bytes,
              };
            } else {
              openPdfWindow(data.pdf_url);
            }
          } else if (data?.pdf_error) {
            message.error({
              content:
                specificPdfErrorMessage(data.pdf_error.code) ??
                `Une erreur est survenue lors de la génération ${
                  draft.PDFRenderState === 'preview' ? "de l'aperçu" : 'du PDF'
                }.`,
              key: 'pdfKey',
            });
            mutateClosePreview(draft);
          } else if (data.pages) {
            const pages = data.pages;
            if (typeof draft.pagesPreviewExcludedIdx === 'number') {
              let pagesPreviewExcluded = [];
              pagesPreviewExcluded = get().pagesPreview.filter(
                (_, idx) => idx < draft.pagesPreviewExcludedIdx
              );
              draft.pagesPreview = [
                ...pagesPreviewExcluded,
                ...formatPagesPreview({
                  pages,
                }),
              ];
            } else {
              draft.pagesPreview = formatPagesPreview({
                pages,
              });
            }
            message.success({
              content: "L'aperçu a bien été généré.",
              key: 'pdfKey',
            });
            draft.isPreviewLoading = false;
          }
          draft.PDFRenderState = false;
        } catch (err) {
          console.error('Error in handlePdfReponse:', err);
          message.error({
            content: 'Une erreur est survenue lors de la génération du PDF.',
            key: 'pdfKey',
          });
          draft.PDFRenderState = false;
          mutateClosePreview(draft);
        }
      }),
      false,
      `handlePdfReponse has ${data?.pdf_url ? 'succeed' : 'failed'}`
    );
  },
  // Échec de la génération levé avant toute réponse (mode PDF local : l'erreur
  // du service est jetée, aucun websocket ne passe par handlePdfReponse) : sans
  // cela les boutons d'export restent grisés.
  resetPdfRender: () => {
    set(
      produce((draft) => {
        draft.PDFRenderState = false;
        mutateClosePreview(draft);
      }),
      false,
      'resetPdfRender'
    );
  },
  declinePdfCompression: () => {
    const pdfUrl = get().pdfCompression?.pdfUrl;
    set(
      produce((draft) => {
        draft.pdfCompression = null;
      }),
      false,
      'declinePdfCompression'
    );
    if (pdfUrl) openPdfWindow(pdfUrl);
  },
  confirmPdfCompression: async () => {
    const pdfCompression = get().pdfCompression;
    if (!pdfCompression || get().isPdfCompressing) return;
    set(
      produce((draft) => {
        draft.isPdfCompressing = true;
      }),
      false,
      'confirmPdfCompression begins'
    );
    try {
      // 202 : le résultat arrivera via le websocket (cf. handlePdfReponse)
      await compressPdf({
        currentDocument: get().currentDocument,
        currentProposal: get().currentProposal,
      });
    } catch (err) {
      console.error('Error in confirmPdfCompression:', err);
      message.error({
        content:
          'La compression du PDF a échoué, le PDF original a été ouvert.',
        key: 'pdfKey',
      });
      openPdfWindow(pdfCompression.pdfUrl);
      set(
        produce((draft) => {
          draft.pdfCompression = null;
          draft.isPdfCompressing = false;
        }),
        false,
        'confirmPdfCompression failed'
      );
    }
  },
});
